import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    TimeScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Bar, Line } from 'react-chartjs-2';
import { getApiUrl } from '@/config/api';
import { authenticatedFetch } from '@/utils/apiUtils';
import { DashboardLayout, Header } from '@/components/shared';
import { useTheme } from '@/contexts/ThemeContext';
import { hexToRgba } from '@/utils/chartColors';
import { formatLrAsymmetryPct, isLrAsymmetryMetricKey } from '@/utils/hawkinAsymmetry';
import ForceTimeCurve from './ForceTimeCurve';
import TestBriefing from './TestBriefing';
import type { BriefingSelection } from './TestBriefing';

ChartJS.register(
    CategoryScale,
    LinearScale,
    TimeScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler,
);

interface TestType {
    id: string;
    name: string;
    test_date: string;
}

interface Athlete {
    id: string;
    name: string;
    teams: string[];
    groups: string[];
    external_id: string;
    invite_status?: 'linked' | 'pending' | 'unlinked';
}

interface HawkinTestData {
    id: string;
    timestamp: number;
    date: string;
    test_type_id: string;
    test_type_name: string;
    segment: string;
    trial_label: string;
    athlete_id: string;
    athlete_name: string;
    jump_height_m: number | null;
    peak_propulsive_force_n: number | null;
    peak_landing_force_n: number | null;
    takeoff_velocity_m_s: number | null;
    peak_velocity_m_s: number | null;
    rsi: number | null;
    mrsi: number | null;
    flight_time_s: number | null;
    countermovement_depth_m: number | null;
    system_weight_n: number | null;
    stiffness_n_m: number | null;
    time_to_takeoff_s: number | null;
    peak_propulsive_power_w: number | null;
    avg_propulsive_force_n: number | null;
    avg_braking_force_n: number | null;
    peak_braking_force_n: number | null;
    braking_rfd_n_s: number | null;
    propulsive_impulse_n_s: number | null;
    braking_impulse_n_s: number | null;
    jump_momentum_kg_m_s: number | null;
    braking_phase_s: number | null;
    propulsive_phase_s: number | null;
    landing_phase_s: number | null;
    time_to_stabilization_ms: number | null;
    peak_relative_propulsive_power_w_kg: number | null;
    peak_relative_propulsive_force_pct: number | null;
    peak_relative_braking_force_pct: number | null;
    lr_avg_propulsive_force_pct: number | null;
    lr_avg_braking_force_pct: number | null;
    lr_peak_braking_force_pct: number | null;
    lr_peak_landing_force_pct: number | null;
    lr_braking_impulse_index_pct: number | null;
    lr_propulsive_impulse_index_pct: number | null;
    landing_height_m: number | null;
    landing_stiffness_n_m: number | null;
    landing_performance_index: number | null;
}

interface TeamSummaryAthlete {
    athlete_id: string;
    athlete_name: string;
    external_id: string;
    test: HawkinTestData;
}

type ViewMode = 'team' | 'athlete';
type TimeRange = '30d' | '90d' | '6mo' | '1yr' | 'all' | 'custom';

const TIME_RANGE_PRESETS: { key: TimeRange; label: string; days: number }[] = [
    { key: '30d', label: '30 days', days: 30 },
    { key: '90d', label: '90 days', days: 90 },
    { key: '6mo', label: '6 months', days: 180 },
    { key: '1yr', label: '1 year', days: 365 },
    { key: 'all', label: 'All time', days: 0 },
];

const METRIC_OPTIONS: { key: keyof HawkinTestData; label: string; unit: string }[] = [
    { key: 'jump_height_m', label: 'Jump Height', unit: 'm' },
    { key: 'peak_propulsive_force_n', label: 'Peak Force', unit: 'N' },
    { key: 'rsi', label: 'RSI', unit: '' },
    { key: 'peak_velocity_m_s', label: 'Peak Velocity', unit: 'm/s' },
    { key: 'takeoff_velocity_m_s', label: 'Takeoff Velocity', unit: 'm/s' },
    { key: 'flight_time_s', label: 'Flight Time', unit: 's' },
    { key: 'peak_propulsive_power_w', label: 'Peak Power', unit: 'W' },
    { key: 'peak_landing_force_n', label: 'Peak Landing Force', unit: 'N' },
    { key: 'countermovement_depth_m', label: 'Countermovement Depth', unit: 'm' },
    { key: 'time_to_takeoff_s', label: 'Time to Takeoff', unit: 's' },
    { key: 'peak_relative_propulsive_power_w_kg', label: 'Relative Peak Power', unit: 'W/kg' },
    { key: 'lr_avg_propulsive_force_pct', label: 'L/R Asymmetry (Propulsive)', unit: '%' },
    { key: 'lr_avg_braking_force_pct', label: 'L/R Asymmetry (Braking)', unit: '%' },
];

const CHART_COLORS = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
    '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16',
    '#06B6D4', '#E11D48', '#059669', '#D97706', '#7C3AED',
];

const HawkinDashboard: React.FC = () => {
    const navigate = useNavigate();
    const { isDark } = useTheme();
    const [searchParams, setSearchParams] = useSearchParams();

    // Synchronous auth gate — redirect immediately, never render dashboard
    const hasToken = Boolean(localStorage.getItem('access'));
    useEffect(() => {
        if (!hasToken) navigate('/login', { replace: true });
    }, [hasToken, navigate]);

    const [userInfo] = useState<{ username: string; role?: string } | null>(() => {
        try { return JSON.parse(localStorage.getItem('user_info') || 'null'); } catch { return null; }
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [testTypes, setTestTypes] = useState<TestType[]>([]);
    const [athletes, setAthletes] = useState<Athlete[]>([]);
    const [selectedTestType, setSelectedTestType] = useState('');
    const [selectedTestDate, setSelectedTestDate] = useState('');
    const [selectedAthleteId, setSelectedAthleteId] = useState('');
    const [selectedMetric, setSelectedMetric] = useState<keyof HawkinTestData>('jump_height_m');
    const [viewMode, setViewMode] = useState<ViewMode>(
        (searchParams.get('view') as ViewMode) || 'team'
    );

    const [teamSummary, setTeamSummary] = useState<TeamSummaryAthlete[]>([]);
    const [teamLoading, setTeamLoading] = useState(false);

    const [athleteTests, setAthleteTests] = useState<HawkinTestData[]>([]);
    const [athleteLoading, setAthleteLoading] = useState(false);
    const [selectedTestId, setSelectedTestId] = useState<string | null>(null);

    const [timeRange, setTimeRange] = useState<TimeRange>('1yr');
    const [customDateFrom, setCustomDateFrom] = useState('');
    const [customDateTo, setCustomDateTo] = useState('');
    const [appliedFrom, setAppliedFrom] = useState('');
    const [appliedTo, setAppliedTo] = useState('');
    const [showAllTests, setShowAllTests] = useState(false);
    const TABLE_PREVIEW_COUNT = 20;

    const applyCustomDates = () => {
        setAppliedFrom(customDateFrom);
        setAppliedTo(customDateTo);
    };

    const fetchWithAuth = useCallback(async (url: string) => {
        const result = await authenticatedFetch(getApiUrl(url), { cache: 'no-store' }, 'Hawkin Dashboard');
        if (!result.success) throw new Error(result.error || 'Request failed');
        return result.data;
    }, []);

    useEffect(() => {
        if (!hasToken) return;
        setLoading(true);
        Promise.all([
            fetchWithAuth('/api/hawkin/test-types/'),
            fetchWithAuth('/api/hawkin/athletes/'),
        ])
            .then(([types, aths]) => {
                if (types) setTestTypes(types);
                if (aths) setAthletes(aths);
                if (types?.length > 0) {
                    setSelectedTestType(types[0].id);
                    setSelectedTestDate(types[0].test_date);
                }
                if (aths?.length > 0 && !selectedAthleteId) setSelectedAthleteId(aths[0].id);
            })
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [hasToken, fetchWithAuth]);

    // No token — render nothing while the redirect effect fires
    if (!hasToken) return null;

    // Fetch team summary whenever test type or date changes (team view)
    useEffect(() => {
        if (!selectedTestType || viewMode !== 'team') return;
        setTeamLoading(true);
        const dateParam = selectedTestDate ? `&test_date=${selectedTestDate}` : '&days=90';
        fetchWithAuth(`/api/hawkin/team-summary/?type_id=${selectedTestType}${dateParam}`)
            .then((data) => { if (data) setTeamSummary(data.athletes || []); })
            .catch((e) => setError(e.message))
            .finally(() => setTeamLoading(false));
    }, [selectedTestType, selectedTestDate, viewMode, fetchWithAuth]);

    // Fetch athlete history (athlete view)
    useEffect(() => {
        if (!selectedAthleteId || viewMode !== 'athlete') return;
        if (timeRange === 'custom' && !appliedFrom && !appliedTo) return;
        setAthleteLoading(true);
        setShowAllTests(false);
        const typeParam = selectedTestType ? `&type_id=${selectedTestType}` : '';

        let dateParams: string;
        if (timeRange === 'custom') {
            const parts: string[] = [];
            if (appliedFrom) parts.push(`from_date=${appliedFrom}`);
            if (appliedTo) parts.push(`to_date=${appliedTo}`);
            dateParams = parts.join('&');
        } else {
            const preset = TIME_RANGE_PRESETS.find((p) => p.key === timeRange);
            let days = preset?.days ?? 365;
            if (days === 0) days = 36500;
            dateParams = `days=${days}`;
        }
        const limitParam = timeRange === 'all' || timeRange === 'custom' ? '&limit=500' : '';

        fetchWithAuth(`/api/hawkin/athletes/${selectedAthleteId}/history/?${dateParams}${typeParam}${limitParam}`)
            .then((data) => {
                if (!data) return;
                const tests: HawkinTestData[] = data.tests || [];
                setAthleteTests(tests);
            })
            .catch((e) => setError(e.message))
            .finally(() => setAthleteLoading(false));
    }, [selectedAthleteId, selectedTestType, viewMode, fetchWithAuth, timeRange, appliedFrom, appliedTo]);

    // Sync search params
    useEffect(() => {
        setSearchParams((prev) => {
            const p = Object.fromEntries(prev);
            return { ...p, view: viewMode };
        });
    }, [viewMode]);

    const handleSwitchView = (mode: ViewMode) => {
        setViewMode(mode);
        setSelectedTestId(null);
        if (mode === 'athlete' && !selectedAthleteId && athletes.length > 0) {
            setSelectedAthleteId(athletes[0].id);
        }
    };

    const handleSelectAthlete = (sel: string | BriefingSelection) => {
        if (typeof sel === 'string') {
            setSelectedAthleteId(sel);
            setSelectedTestId(null);
            setViewMode('athlete');
            return;
        }
        setSelectedAthleteId(sel.athleteId);
        if (sel.metricKey) {
            const match = METRIC_OPTIONS.find((m) => m.key === sel.metricKey);
            if (match) setSelectedMetric(match.key);
        }
        setSelectedTestId(sel.testId ?? null);
        setViewMode('athlete');
    };

    const shell = (children: React.ReactNode) => (
        <div className="min-h-screen cgm-bg-primary">
            <Header userInfo={userInfo} isDark={isDark} />
            <DashboardLayout>{children}</DashboardLayout>
        </div>
    );

    if (loading) {
        return shell(
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 cgm-spinner" />
            </div>
        );
    }

    if (error) {
        const hasNoData = testTypes.length === 0 && athletes.length === 0;
        const isHawkinError = hasNoData
            || error.includes('Hawkin') || error.includes('hawkin')
            || error.includes('404');
        return shell(
            <div className="max-w-lg mx-auto mt-12">
                <div className="cgm-card p-10 text-center space-y-4" style={{ transition: 'none' }}>
                    <div className="mx-auto w-14 h-14 flex items-center justify-center">
                        <img
                            src={isDark ? '/logos/hawkin/hawkin-icon-white.webp' : '/logos/hawkin/hawkin-icon-black.webp'}
                            alt="Hawkin Dynamics"
                            className="w-full h-full object-contain"
                        />
                    </div>
                    <h2 className="text-xl font-semibold cgm-text-primary">
                        {isHawkinError ? 'Hawkin Dynamics Not Connected' : 'Something went wrong'}
                    </h2>
                    <p className="text-sm cgm-text-secondary leading-relaxed">
                        {isHawkinError
                            ? 'Connect your Hawkin Dynamics account to access force plate data, jump analytics, and asymmetry insights.'
                            : error}
                    </p>
                    <button
                        onClick={() => isHawkinError ? navigate('/connect-hawkin') : window.location.reload()}
                        className="cgm-button inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium mt-2"
                    >
                        {isHawkinError ? 'Connect Hawkin Dynamics' : 'Retry'}
                    </button>
                </div>
            </div>
        );
    }

    if (testTypes.length === 0 && athletes.length === 0) {
        return shell(
            <div className="max-w-lg mx-auto mt-12">
                <div className="cgm-card p-10 text-center space-y-4" style={{ transition: 'none' }}>
                    <div className="mx-auto w-14 h-14 flex items-center justify-center">
                        <img
                            src={isDark ? '/logos/hawkin/hawkin-icon-white.webp' : '/logos/hawkin/hawkin-icon-black.webp'}
                            alt="Hawkin Dynamics"
                            className="w-full h-full object-contain"
                        />
                    </div>
                    <h2 className="text-xl font-semibold cgm-text-primary">
                        No Test Data Yet
                    </h2>
                    <p className="text-sm cgm-text-secondary leading-relaxed">
                        Hawkin is connected but no test data has been recorded yet.
                        Data will appear here once athletes complete force plate tests.
                    </p>
                    <button
                        onClick={() => window.location.reload()}
                        className="cgm-button inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium mt-2"
                    >
                        Refresh
                    </button>
                </div>
            </div>
        );
    }

    const metricInfo = METRIC_OPTIONS.find((m) => m.key === selectedMetric) || METRIC_OPTIONS[0];

    const viewToggle = (
        <div className="flex items-center gap-0">
            {(['team', 'athlete'] as const).map((mode) => (
                <button
                    key={mode}
                    onClick={() => handleSwitchView(mode)}
                    className={`px-3 py-1.5 text-sm font-medium transition-all cursor-pointer ${
                        viewMode === mode
                            ? 'cgm-tab-active'
                            : 'cgm-text-secondary hover:cgm-text-primary'
                    }`}
                >
                    {mode === 'team' ? 'Team' : 'Athlete'}
                </button>
            ))}
        </div>
    );

    /* ── Team View ─────────────────────────────────────────────── */

    const renderTeamView = () => {
        const validAthletes = teamSummary.filter(
            (a) => a.test[selectedMetric as keyof HawkinTestData] != null
        );
        const lrMetric = isLrAsymmetryMetricKey(selectedMetric as string);
        const sorted = [...validAthletes].sort((a, b) => {
            const va = a.test[selectedMetric as keyof HawkinTestData] as number;
            const vb = b.test[selectedMetric as keyof HawkinTestData] as number;
            if (lrMetric) return Math.abs(vb) - Math.abs(va);
            return vb - va;
        });
        const labels = sorted.map((a) => a.athlete_name);
        const values = sorted.map((a) => a.test[selectedMetric as keyof HawkinTestData] as number);

        const barData = {
            labels,
            datasets: [
                {
                    label: `${metricInfo.label}${metricInfo.unit ? ` (${metricInfo.unit})` : ''}`,
                    data: values,
                    backgroundColor: lrMetric
                        ? values.map((v) => v > 0 ? hexToRgba('#3B82F6', 0.7) : hexToRgba('#EF4444', 0.7))
                        : sorted.map((_, i) => hexToRgba(CHART_COLORS[i % CHART_COLORS.length], 0.7)),
                    borderColor: lrMetric
                        ? values.map((v) => v > 0 ? '#3B82F6' : '#EF4444')
                        : sorted.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
                    borderWidth: 1,
                    borderRadius: 4,
                },
            ],
        };

        return (
            <div className="space-y-6">
                {/* Controls */}
                <div className="flex flex-wrap gap-4 items-end">
                    <div className="flex-1 min-w-[240px]">
                        <label className="block text-xs cgm-text-secondary mb-1">Test</label>
                        <select
                            value={`${selectedTestType}|${selectedTestDate}`}
                            onChange={(e) => {
                                const [typeId, date] = e.target.value.split('|');
                                setSelectedTestType(typeId);
                                setSelectedTestDate(date || '');
                            }}
                            className="cgm-input w-full text-sm"
                        >
                            {testTypes.map((t) => (
                                <option key={`${t.id}|${t.test_date}`} value={`${t.id}|${t.test_date}`}>
                                    {t.name} — {t.test_date}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Symmetry + trends */}
                <TestBriefing
                    testTypeId={selectedTestType}
                    testDate={selectedTestDate}
                    onSelectAthlete={handleSelectAthlete}
                />

                <div className="max-w-md">
                    <label className="block text-xs cgm-text-secondary mb-1">Metric</label>
                    <select
                        value={selectedMetric}
                        onChange={(e) => setSelectedMetric(e.target.value as keyof HawkinTestData)}
                        className="cgm-input w-full text-sm"
                    >
                        {METRIC_OPTIONS.map((m) => (
                            <option key={m.key} value={m.key}>
                                {m.label}{m.unit ? ` (${m.unit})` : ''}
                            </option>
                        ))}
                    </select>
                </div>

                {teamLoading ? (
                    <div className="flex items-center justify-center h-48">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 cgm-spinner" />
                    </div>
                ) : sorted.length === 0 ? (
                    <div className="text-center py-12 cgm-text-secondary">
                        No data for this test type and metric in the last 90 days.
                    </div>
                ) : (
                    <>
                        {/* Bar chart */}
                        <div className="cgm-card p-4">
                            <Bar
                                data={barData}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    indexAxis: 'y' as const,
                                    plugins: {
                                        legend: { display: false },
                                        tooltip: {
                                            callbacks: {
                                                label: (ctx) => {
                                                    const idx = ctx.dataIndex;
                                                    const raw = sorted[idx].test[selectedMetric as keyof HawkinTestData] as number;
                                                    if (lrMetric) return formatLrAsymmetryPct(raw);
                                                    return `${ctx.parsed.x?.toFixed(3)}${metricInfo.unit ? ` ${metricInfo.unit}` : ''}`;
                                                },
                                            },
                                        },
                                    },
                                    scales: {
                                        x: (() => {
                                            const base: any = {
                                                title: {
                                                    display: true,
                                                    text: lrMetric ? '← R dominant  |  L dominant →' : `${metricInfo.label}${metricInfo.unit ? ` (${metricInfo.unit})` : ''}`,
                                                    color: isDark ? '#9CA3AF' : '#6B7280',
                                                },
                                                ticks: {
                                                    color: isDark ? '#9CA3AF' : '#6B7280',
                                                    callback: lrMetric ? (val: any) => `${Math.abs(val)}%` : undefined,
                                                },
                                                grid: { color: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' },
                                            };
                                            if (lrMetric) {
                                                const maxAbs = Math.max(...values.map(Math.abs), 1);
                                                base.suggestedMin = -maxAbs;
                                                base.suggestedMax = maxAbs;
                                            }
                                            return base;
                                        })(),
                                        y: {
                                            ticks: { color: isDark ? '#D1D5DB' : '#374151' },
                                            grid: { display: false },
                                        },
                                    },
                                    onClick: (_evt, elements) => {
                                        if (elements.length > 0) {
                                            const idx = elements[0].index;
                                            handleSelectAthlete(sorted[idx].athlete_id);
                                        }
                                    },
                                }}
                                height={Math.max(300, sorted.length * 32)}
                            />
                        </div>

                        {/* Force-Time Curve (team view — click a row to expand) */}
                        {selectedTestId && (
                            <ForceTimeCurve
                                testId={selectedTestId}
                                onClose={() => setSelectedTestId(null)}
                            />
                        )}

                        {/* Table */}
                        <div className="cgm-card overflow-x-auto">
                            <h3 className="text-sm font-medium cgm-text-primary p-3 border-b border-gray-200 dark:border-gray-700">
                                Team Results — click a row to view force-time curve
                            </h3>
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-gray-200 dark:border-gray-700">
                                        <th className="text-left p-3 cgm-text-secondary font-medium">Athlete</th>
                                        <th className="text-left p-3 cgm-text-secondary font-medium">Date</th>
                                        <th className="text-right p-3 cgm-text-secondary font-medium">Jump Height (m)</th>
                                        <th className="text-right p-3 cgm-text-secondary font-medium">Peak Force (N)</th>
                                        <th className="text-right p-3 cgm-text-secondary font-medium">RSI</th>
                                        <th className="text-right p-3 cgm-text-secondary font-medium">Peak Power (W)</th>
                                        <th className="w-10"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sorted.map((a) => (
                                        <tr
                                            key={a.athlete_id}
                                            className={`border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer ${
                                                selectedTestId === a.test.id ? 'cgm-card-selected' : ''
                                            }`}
                                            onClick={() => setSelectedTestId(selectedTestId === a.test.id ? null : a.test.id)}
                                        >
                                            <td className="p-3 cgm-text-primary font-medium">{a.athlete_name}</td>
                                            <td className="p-3 cgm-text-secondary">{a.test.date}</td>
                                            <td className="p-3 text-right cgm-text-primary">{a.test.jump_height_m?.toFixed(3) ?? '—'}</td>
                                            <td className="p-3 text-right cgm-text-primary">{a.test.peak_propulsive_force_n?.toFixed(0) ?? '—'}</td>
                                            <td className="p-3 text-right cgm-text-primary">{a.test.rsi?.toFixed(3) ?? '—'}</td>
                                            <td className="p-3 text-right cgm-text-primary">{a.test.peak_propulsive_power_w?.toFixed(0) ?? '—'}</td>
                                            <td className="p-3 text-center cgm-text-secondary text-xs">
                                                {selectedTestId === a.test.id ? '▲' : '▶'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        );
    };

    const clampDateValue = (val: string): string => {
        if (!val) return val;
        const [y, m, d] = val.split('-');
        if (!y || !m || !d) return val;
        return `${y.slice(0, 4).padStart(4, '0')}-${m.slice(0, 2)}-${d.slice(0, 2)}`;
    };

    /* ── Athlete View ──────────────────────────────────────────── */

    const linkedCount = athletes.filter((a) => !a.invite_status || a.invite_status === 'linked').length;
    const pendingCount = athletes.filter((a) => a.invite_status === 'pending').length;
    const unlinkedCount = athletes.filter((a) => a.invite_status === 'unlinked').length;

    const managePlayersNav = () => navigate('/settings', { state: { openManagePlayers: 'hawkin' } });

    const statusPills = (
        <div className="flex items-center gap-2">
            {linkedCount > 0 && (
                <button
                    onClick={managePlayersNav}
                    className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-opacity hover:opacity-80"
                    style={{ backgroundColor: 'rgba(16, 185, 129, 0.12)' }}
                    title={`${linkedCount} player${linkedCount !== 1 ? 's' : ''} linked. Click to manage players.`}
                >
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span className="text-emerald-600 dark:text-emerald-400">{linkedCount}</span>
                </button>
            )}
            {pendingCount > 0 && (
                <button
                    onClick={managePlayersNav}
                    className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-opacity hover:opacity-80"
                    style={{ backgroundColor: 'rgba(245, 158, 11, 0.12)' }}
                    title={`${pendingCount} player${pendingCount !== 1 ? 's' : ''} invited but haven't joined yet. Click to manage players.`}
                >
                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                    <span className="text-amber-600 dark:text-amber-400">{pendingCount}</span>
                </button>
            )}
            {unlinkedCount > 0 && (
                <button
                    onClick={managePlayersNav}
                    className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-opacity hover:opacity-80"
                    style={{ backgroundColor: 'rgba(239, 68, 68, 0.12)' }}
                    title={`${unlinkedCount} player${unlinkedCount !== 1 ? 's' : ''} not linked to an account. Click to manage players.`}
                >
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    <span className="text-red-600 dark:text-red-400">{unlinkedCount}</span>
                </button>
            )}
        </div>
    );

    const renderAthleteView = () => {
        const athlete = athletes.find((a) => a.id === selectedAthleteId);
        const validTests = athleteTests.filter((t) => t[selectedMetric] != null);
        const reversedTests = [...validTests].reverse();
        const visibleTests = showAllTests ? reversedTests : reversedTests.slice(0, TABLE_PREVIEW_COUNT);
        const lrLine = isLrAsymmetryMetricKey(selectedMetric as string);

        const lineData = {
            datasets: [
                {
                    label: `${metricInfo.label}${metricInfo.unit ? ` (${metricInfo.unit})` : ''}`,
                    data: validTests.map((t) => ({ x: t.date, y: t[selectedMetric] as number })),
                    borderColor: '#3B82F6',
                    backgroundColor: hexToRgba('#3B82F6', 0.1),
                    fill: true,
                    tension: 0,
                    pointRadius: 4,
                    pointHoverRadius: 6,
                },
            ],
        };

        return (
            <div className="space-y-6">
                {/* Controls */}
                <div className="flex flex-wrap gap-4 items-end">
                    <div className="flex-1 min-w-[200px]">
                        <label className="block text-xs cgm-text-secondary mb-1">Athlete</label>
                        <select
                            value={selectedAthleteId}
                            onChange={(e) => setSelectedAthleteId(e.target.value)}
                            className="cgm-input w-full text-sm"
                        >
                            {athletes.map((a) => {
                                const suffix = a.invite_status === 'unlinked' ? ' (Unlinked)'
                                    : a.invite_status === 'pending' ? ' (Pending)'
                                    : '';
                                return (
                                    <option key={a.id} value={a.id}>
                                        {a.name}{suffix}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                    <div className="flex-1 min-w-[240px]">
                        <label className="block text-xs cgm-text-secondary mb-1">Test Type</label>
                        <select
                            value={selectedTestType}
                            onChange={(e) => setSelectedTestType(e.target.value)}
                            className="cgm-input w-full text-sm"
                        >
                            <option value="">All types</option>
                            {[...new Map(testTypes.map((t) => [t.id, t])).values()].map((t) => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <label className="block text-xs cgm-text-secondary mb-1">Metric</label>
                        <select
                            value={selectedMetric}
                            onChange={(e) => setSelectedMetric(e.target.value as keyof HawkinTestData)}
                            className="cgm-input w-full text-sm"
                        >
                            {METRIC_OPTIONS.map((m) => (
                                <option key={m.key} value={m.key}>
                                    {m.label}{m.unit ? ` (${m.unit})` : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Time range controls */}
                <div className="cgm-card p-3 space-y-3">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <label className="text-xs cgm-text-secondary font-medium shrink-0">History</label>
                        <div className="flex items-center gap-1 flex-wrap">
                            {TIME_RANGE_PRESETS.map((p) => (
                                <button
                                    key={p.key}
                                    onClick={() => setTimeRange(p.key)}
                                    className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer ${
                                        timeRange === p.key
                                            ? 'bg-blue-600 text-white shadow-sm'
                                            : 'cgm-text-secondary hover:cgm-text-primary hover:bg-gray-100 dark:hover:bg-gray-700'
                                    }`}
                                >
                                    {p.label}
                                </button>
                            ))}
                            <button
                                onClick={() => setTimeRange('custom')}
                                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer ${
                                    timeRange === 'custom'
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'cgm-text-secondary hover:cgm-text-primary hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                            >
                                Custom
                            </button>
                        </div>
                    </div>
                    {timeRange === 'custom' && (
                        <div className="flex items-end gap-3 flex-wrap">
                            <div className="min-w-[150px]">
                                <label className="block text-xs cgm-text-secondary mb-1">From</label>
                                <input
                                    type="date"
                                    value={customDateFrom}
                                    min="2000-01-01"
                                    max={customDateTo || new Date().toISOString().split('T')[0]}
                                    onChange={(e) => setCustomDateFrom(clampDateValue(e.target.value))}
                                    onKeyDown={(e) => { if (e.key === 'Enter') applyCustomDates(); }}
                                    className="cgm-input w-full text-sm"
                                />
                            </div>
                            <div className="min-w-[150px]">
                                <label className="block text-xs cgm-text-secondary mb-1">To</label>
                                <input
                                    type="date"
                                    value={customDateTo}
                                    min={customDateFrom || '2000-01-01'}
                                    max={new Date().toISOString().split('T')[0]}
                                    onChange={(e) => setCustomDateTo(clampDateValue(e.target.value))}
                                    onKeyDown={(e) => { if (e.key === 'Enter') applyCustomDates(); }}
                                    className="cgm-input w-full text-sm"
                                />
                            </div>
                            <button
                                onClick={applyCustomDates}
                                disabled={!customDateFrom && !customDateTo}
                                className="px-3 py-1.5 text-xs font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Apply
                            </button>
                            {(customDateFrom || customDateTo) && (
                                <button
                                    onClick={() => { setCustomDateFrom(''); setCustomDateTo(''); setAppliedFrom(''); setAppliedTo(''); }}
                                    className="text-xs cgm-text-secondary hover:cgm-text-primary underline pb-2 cursor-pointer"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {athleteLoading ? (
                    <div className="flex items-center justify-center h-48">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 cgm-spinner" />
                    </div>
                ) : validTests.length === 0 ? (
                    <div className="text-center py-12 cgm-text-secondary">
                        No {metricInfo.label.toLowerCase()} data for {athlete?.name || 'this athlete'}.
                    </div>
                ) : (
                    <>
                        {/* Line chart */}
                        <div className="cgm-card p-4">
                            <h3 className="text-sm font-medium cgm-text-primary mb-3">
                                {athlete?.name} — {metricInfo.label} Over Time
                            </h3>
                            <div style={{ height: 300 }}>
                                <Line
                                    data={lineData}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: {
                                            legend: { display: false },
                                            tooltip: {
                                                callbacks: {
                                                    title: (items) => items[0]?.label ?? '',
                                                    label: (ctx) => {
                                                        const i = ctx.dataIndex;
                                                        const raw = validTests[i][selectedMetric] as number;
                                                        if (lrLine) return formatLrAsymmetryPct(raw);
                                                        return `${ctx.parsed.y?.toFixed(3)}${metricInfo.unit ? ` ${metricInfo.unit}` : ''}`;
                                                    },
                                                },
                                            },
                                        },
                                        scales: {
                                            x: {
                                                type: 'time' as const,
                                                time: {
                                                    unit: validTests.length > 60 ? 'month' : 'week',
                                                    tooltipFormat: 'MMM d, yyyy',
                                                    displayFormats: { week: 'MMM d', month: 'MMM yyyy' },
                                                },
                                                ticks: { color: isDark ? '#9CA3AF' : '#6B7280', maxRotation: 45 },
                                                grid: { color: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' },
                                            },
                                            y: (() => {
                                                const base: any = {
                                                    title: {
                                                        display: true,
                                                        text: lrLine ? '← R dominant  |  L dominant →' : `${metricInfo.label}${metricInfo.unit ? ` (${metricInfo.unit})` : ''}`,
                                                        color: isDark ? '#9CA3AF' : '#6B7280',
                                                    },
                                                    ticks: {
                                                        color: isDark ? '#9CA3AF' : '#6B7280',
                                                        callback: lrLine ? (val: any) => `${Math.abs(val)}%` : undefined,
                                                    },
                                                    grid: { color: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' },
                                                };
                                                if (lrLine) {
                                                    const lineVals = validTests.map((t) => t[selectedMetric] as number);
                                                    const maxAbs = Math.max(...lineVals.map(Math.abs), 1);
                                                    base.suggestedMin = -maxAbs;
                                                    base.suggestedMax = maxAbs;
                                                }
                                                return base;
                                            })(),
                                        },
                                    }}
                                />
                            </div>
                        </div>

                        {/* Force-Time Curve (athlete view) */}
                        {selectedTestId && (
                            <ForceTimeCurve
                                testId={selectedTestId}
                                onClose={() => setSelectedTestId(null)}
                            />
                        )}

                        {/* Tests table */}
                        <div className="cgm-card overflow-x-auto">
                            <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700">
                                <h3 className="text-sm font-medium cgm-text-primary">
                                    Tests ({reversedTests.length}) — click a row to view force-time curve
                                </h3>
                                {reversedTests.length > TABLE_PREVIEW_COUNT && (
                                    <button
                                        onClick={() => setShowAllTests(!showAllTests)}
                                        className="text-xs text-blue-500 hover:text-blue-600 font-medium cursor-pointer"
                                    >
                                        {showAllTests ? 'Show recent' : `Show all ${reversedTests.length}`}
                                    </button>
                                )}
                            </div>
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-gray-200 dark:border-gray-700">
                                        <th className="text-left p-3 cgm-text-secondary font-medium">Date</th>
                                        <th className="text-left p-3 cgm-text-secondary font-medium">Test Type</th>
                                        <th className="text-right p-3 cgm-text-secondary font-medium">Jump Height (m)</th>
                                        <th className="text-right p-3 cgm-text-secondary font-medium">Peak Force (N)</th>
                                        <th className="text-right p-3 cgm-text-secondary font-medium">RSI</th>
                                        <th className="text-right p-3 cgm-text-secondary font-medium">Peak Power (W)</th>
                                        <th className="w-10"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleTests.map((t) => (
                                        <tr
                                            key={t.id}
                                            className={`border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer ${
                                                selectedTestId === t.id ? 'cgm-card-selected' : ''
                                            }`}
                                            onClick={() => setSelectedTestId(selectedTestId === t.id ? null : t.id)}
                                        >
                                            <td className="p-3 cgm-text-primary">{t.date}</td>
                                            <td className="p-3 cgm-text-secondary">
                                                {t.test_type_name}
                                                {t.trial_label && <span className="ml-1 text-xs opacity-60">({t.trial_label})</span>}
                                            </td>
                                            <td className="p-3 text-right cgm-text-primary">{t.jump_height_m?.toFixed(3) ?? '—'}</td>
                                            <td className="p-3 text-right cgm-text-primary">{t.peak_propulsive_force_n?.toFixed(0) ?? '—'}</td>
                                            <td className="p-3 text-right cgm-text-primary">{t.rsi?.toFixed(3) ?? '—'}</td>
                                            <td className="p-3 text-right cgm-text-primary">{t.peak_propulsive_power_w?.toFixed(0) ?? '—'}</td>
                                            <td className="p-3 text-center cgm-text-secondary text-xs">
                                                {selectedTestId === t.id ? '▲' : '▶'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {!showAllTests && reversedTests.length > TABLE_PREVIEW_COUNT && (
                                <div className="p-3 text-center border-t border-gray-200 dark:border-gray-700">
                                    <button
                                        onClick={() => setShowAllTests(true)}
                                        className="text-xs text-blue-500 hover:text-blue-600 font-medium cursor-pointer"
                                    >
                                        Show {reversedTests.length - TABLE_PREVIEW_COUNT} more tests
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        );
    };

    return shell(
        <div className="space-y-6">
            {/* Header row */}
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-semibold cgm-text-primary">
                        Hawkin Dynamics
                    </h1>
                    {statusPills}
                </div>
                {viewToggle}
            </div>

            {viewMode === 'team' ? renderTeamView() : renderAthleteView()}
        </div>
    );
};

export default HawkinDashboard;
